import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, MaskLines, SectionLabel } from "./Reveal";
import { SERVICES } from "@/lib/data";
import { VehicleTypes } from "./VehicleTypes";
import { useLang } from "@/lib/i18n";

const SERVICE_VALUE = {
  "car-unlock": "Car Unlock", "smart-keys": "Smart Key", ecu: "Computer / ECU Programming", tcm: "Transmission / TCM Programming",
  "key-recovery": "Key Recovery (all keys lost)", "lock-repair": "Lock Repair & Maintenance", "remote-keys": "Remote Key",
  "flip-keys": "Flip Key", "remote-control": "Remote Control", "replacement-key": "Replacement Key", adas: "ADAS Calibration",
};

const ServiceCard = ({ s, i }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [title, kicker, desc] = t.services.items[s.id] || [s.title, s.kicker, s.desc];
  const dark = !!s.img;
  return (
  <FadeUp delay={(i % 4) * 0.06} className={`col-span-1 ${s.span}`}>
    <motion.button
      data-testid={`service-card-${s.id}`}
      onClick={() => navigate(`/quote?service=${encodeURIComponent(SERVICE_VALUE[s.id] || "")}`)}
      whileHover="hover"
      className={`group relative flex h-full w-full flex-col justify-between overflow-hidden border border-white/10 bg-coal p-6 text-left transition-colors duration-500 hover:border-brand/50 md:p-8 ${s.tall ? "min-h-[380px] md:min-h-[460px]" : "min-h-[260px]"}`}
    >
      {s.img && (
        <>
          <motion.img variants={{ hover: { scale: 1.06 } }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }} src={s.img} alt={title} className="absolute inset-0 h-full w-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/80 to-forest/25" />
        </>
      )}
      <div className="relative flex items-start justify-between">
        <span className={`font-mono text-[12px] tracking-[0.2em] ${dark ? "text-[#F2C14E]" : "text-brand"}`}>{s.n}</span>
        <span className={`grid h-10 w-10 place-items-center border transition-[background-color,color,transform] duration-500 group-hover:rotate-45 group-hover:bg-brand group-hover:text-cream ${dark ? "border-cream/30 text-cream" : "border-white/15 text-bone"}`}>
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </div>
      <div className="relative mt-10">
        <div className={`font-mono text-[11px] uppercase tracking-[0.2em] ${dark ? "text-cream/80" : "text-zinc-400"}`}>{kicker}</div>
        <h3 className={`font-display mt-3 font-extrabold uppercase leading-[0.95] ${dark ? "text-cream" : "text-bone"} ${s.tall ? "text-3xl md:text-5xl" : "text-xl md:text-2xl"}`}>{title}</h3>
        <p className={`mt-4 max-w-md text-sm leading-relaxed md:text-base ${dark ? "text-cream/90" : "text-zinc-300"}`}>{desc}</p>
      </div>
    </motion.button>
  </FadeUp>
  );
};

export const Services = () => {
  const { t } = useLang();
  const s = t.services;
  return (
  <section id="services" data-testid="services-section" className="relative bg-ink py-24 md:py-36">
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionLabel n="01">{s.label}</SectionLabel>
          <h2 className="font-display mt-8 text-4xl font-black uppercase leading-[0.92] text-bone sm:text-5xl lg:text-6xl">
            <MaskLines inView lines={[s.h1, <span key="b">{s.h2a} <span className="font-serif-i normal-case text-brand">{s.h2b}</span></span>]} />
          </h2>
        </div>
        <FadeUp className="self-end lg:col-span-5">
          <p className="text-base leading-relaxed text-zinc-300 md:text-lg">
            {s.intro}
          </p>
        </FadeUp>
      </div>
      <div className="mt-16 grid grid-cols-1 gap-3 md:grid-cols-12 md:gap-4">
        {SERVICES.map((sv, i) => <ServiceCard key={sv.id} s={sv} i={i} />)}
      </div>
      <VehicleTypes />
    </div>
  </section>
  );
};
